// apps/booking-frontend/src/components/BookingLimitBanner.tsx
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { getBookingLimitInfo } from '../services/api';
import { isSubscriptionEnabled } from '../utils/environment';

interface BookingLimitInfo {
  isWithinLimit: boolean;
  currentCount: number;
  limit: number;
  planName: string;
  exceeded: number;
}

interface BookingLimitBannerProps {
  onLimitChange?: (canAddBooking: boolean) => void;
  showAlways?: boolean; // 管理員頁面始終顯示
  refreshTrigger?: number; // 當這個值變化時，重新獲取預約限制資訊
}

const BookingLimitBanner: React.FC<BookingLimitBannerProps> = ({ onLimitChange, showAlways = false, refreshTrigger }) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { user } = userContext!;

  // Docker 版本不顯示預約限制橫幅
  if (!isSubscriptionEnabled()) {
    // 仍然需要通知父組件可以新增預約（沒有限制）
    useEffect(() => {
      if (onLimitChange) {
        onLimitChange(true);
      }
    }, [onLimitChange]);
    return null;
  }

  // 嘗試從 localStorage 恢復之前的狀態，避免初次載入時的跳動
  const [limitInfo, setLimitInfo] = useState<BookingLimitInfo | null>(() => {
    if (!user?.organizationId) return null;
    try {
      const cached = localStorage.getItem(`booking-limit-${user.organizationId}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [isInitialLoading, setIsInitialLoading] = useState(!limitInfo);

  const fetchLimitInfo = useCallback(async (isInitial = false) => {
    if (!user?.id) return;

    try {
      if (isInitial) {
        setIsInitialLoading(true);
      }

      const response = await getBookingLimitInfo();
      const data = response.data;
      setLimitInfo(data);

      try {
        localStorage.setItem(`booking-limit-${user.organizationId}`, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to cache booking limit info:', error);
      }

      if (onLimitChange) {
        onLimitChange(data.isWithinLimit);
      }
    } catch (error) {
      console.error('Failed to fetch booking limit info:', error);
    } finally {
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  }, [user?.id, user?.organizationId, onLimitChange]);

  useEffect(() => {
    if (user?.id) {
      fetchLimitInfo(true);
    }
  }, [user?.id, fetchLimitInfo]);

  useEffect(() => {
    if (user?.id && refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchLimitInfo(false);
    }
  }, [refreshTrigger, user?.id, fetchLimitInfo]);

  if (isInitialLoading && !limitInfo) {
    return null;
  }

  if (!limitInfo) return null;

  // 一般頁面只在超過限制時顯示，管理員頁面始終顯示
  if (!showAlways && limitInfo.isWithinLimit) {
    return null;
  }

  const getStatusColor = () => {
    if (limitInfo.isWithinLimit) {
      return 'bg-green-50 border-green-200 text-green-800';
    } else {
      return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  const getStatusIcon = () => {
    return limitInfo.isWithinLimit ? '✅' : '⚠️';
  };

  const getProgressPercentage = () => {
    if (limitInfo.limit === 0) return 0;
    return Math.min((limitInfo.currentCount / limitInfo.limit) * 100, 100);
  };

  return (
    <div className={`rounded-md p-4 mb-6 border ${getStatusColor()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-xl">{getStatusIcon()}</span>
          <div>
            <h3 className="font-semibold text-sm">
              {limitInfo.limit === 0 ? (
                <Trans
                  key="unlimited"
                  i18nKey="resourceLimit:unlimitedUsage"
                  values={{
                    planName: t(`resourceLimit:plans.${limitInfo.planName}`, limitInfo.planName),
                    currentCount: limitInfo.currentCount,
                    entityName: t('translation:entityNames.booking')
                  }}
                  components={{ bold: <strong key="bold-unlimited" /> }}
                />
              ) : (
                <span key="limited">
                  <Trans
                    key="limited-usage"
                    i18nKey="resourceLimit:limitedUsage"
                    values={{
                      planName: t(`resourceLimit:plans.${limitInfo.planName}`, limitInfo.planName),
                      currentCount: limitInfo.currentCount,
                      limit: limitInfo.limit,
                      entityName: t('translation:entityNames.booking')
                    }}
                    components={{ bold: <strong key="bold-limited" /> }}
                  />
                  {!limitInfo.isWithinLimit && (
                    <span key="exceeded" className="ml-2 text-red-600 font-medium">
                      {t('resourceLimit:exceeded', { exceeded: limitInfo.exceeded })}
                    </span>
                  )}
                </span>
              )}
            </h3>
            {!limitInfo.isWithinLimit && (
              <p className="text-sm mt-1 opacity-75">
                {t('resourceLimit:upgradeOrWaitForReset', { entityName: t('translation:entityNames.booking') })}
              </p>
            )}
          </div>
        </div>

        {limitInfo.limit > 0 && (
          <div className="flex items-center space-x-3">
            <div className="w-32 bg-slate-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  limitInfo.isWithinLimit ? 'bg-green-500' : 'bg-red-500'
                }`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
            <span className="text-sm font-medium">
              {Math.round(getProgressPercentage())}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingLimitBanner;
